import { defineMessages } from './define'

/* The app shell and the furniture every screen shares: the tab bar, route
 * gates, sign-in, the lock screen, onboarding, help, search, and the cards and
 * dialogs in ../shared.tsx. */

export default defineMessages({
  en: {
    'shell.signInHead': 'Sign in to {app}',
    'shell.signInSub': 'Staff, doctors and patients',
    // route gates and the app frame
    'shell.notAvailable': 'Not available',
    'shell.noAccessTitle': 'Your role does not have access to this screen.',
    'shell.noAccessBody': 'Ask an administrator if you think you should.',
    'shell.loadingScreen': 'Loading screen…',
    'shell.offlineBanner': 'Can’t reach the clinic server — showing what was saved at {time}. Changes need the connection back.',

    // tab bar
    'shell.sections': 'Sections',
    'shell.tabHome': 'Home',
    'shell.tabMyVisits': 'My visits',
    'shell.tabBookings': 'Bookings',
    'shell.tabMyRecord': 'My record',
    'shell.tabProfile': 'Profile',

    // welcome
    'shell.w1Title': 'The clinic in your pocket',
    'shell.w1Lead': 'Every desktop screen has a counterpart here. Same login, same records, same rules - sized for the hand you have free between patients.',
    'shell.w1P1': 'Nothing is a second copy: the phone and the desk talk to one API',
    'shell.w1P2': 'What you may open follows your role, exactly as it does on the desk',
    'shell.w1P3': 'The desktop app is always one tap away from your profile',
    'shell.w2Title': 'From the queue to the pad',
    'shell.w2Lead': 'Reception opens a visit and the patient gets a token for that doctor, for that day. The consulting room picks the queue up from there.',
    'shell.w2P1': 'Tokens restart per doctor each morning, so two doctors can both be on 4',
    'shell.w2P2': 'Vitals are appended, never overwritten - every reading keeps its time',
    'shell.w2P3': 'Signing the prescription closes it; corrections go in as an addendum',
    'shell.w3Title': 'Records that still add up',
    'shell.w3Lead': 'Past visits, advised tests and the day book are all here, and every amount you see traces back to a row somebody entered.',
    'shell.w3P1': 'Lab tests are booked, paid and tracked from the lab screen',
    'shell.w3P2': 'Discounts need a reason and paid visits are corrected by adjustment',
    'shell.w3P3': 'Allergies follow the patient onto every card and every printout',
    'shell.goToStep': 'Go to step {n}',
    'shell.skip': 'Skip',
    'shell.getStarted': 'Get started',

    // lock
    'shell.enterPin': 'Enter your PIN',
    'shell.stillSignedIn': '{name} is still signed in on this device.',
    'shell.deviceLocked': 'This device is locked.',
    'shell.pinEntry': 'PIN entry',
    'shell.pinWrong': 'That PIN is not right. Try again.',
    'shell.fourDigits': 'Four digits.',
    'shell.deleteDigit': 'Delete last digit',
    'shell.signOutInstead': 'Sign out instead',
    'shell.forgotPin': 'Forgotten the PIN? Signing out clears it, and signing back in with your clinic password lets you set a new one.',

    // sign-in
    'shell.couldNotSignIn': 'Could not sign in.',
    'shell.welcomeBack': 'Welcome back',
    'shell.signInTo': 'Sign in to {clinic}.',
    'shell.passwordCleared': 'The password box has been cleared. If your browser filled it in, that saved password is out of date - type the current one instead.',
    'shell.username': 'Username',
    'shell.password': 'Password',
    'shell.hidePassword': 'Hide password',
    'shell.showPassword': 'Show password',
    'shell.signingIn': 'Signing in…',
    'shell.signIn': 'Sign in',
    'shell.accountsByAdmin': 'Accounts are created by the clinic administrator. If you are locked out, ask them to reset your password rather than trying again.',

    // help
    'shell.help': 'Help',
    'shell.faq': 'FAQ',
    'shell.contact': 'Contact',
    'shell.howSystemBehaves': 'How this system behaves',
    'shell.thisClinic': 'This clinic',
    'shell.phone': 'Phone',
    'shell.alternate': 'Alternate',
    'shell.opdHours': 'OPD hours',
    'shell.whoToAsk': 'Who to ask',
    'shell.helpRunByClinic': 'This system is run by the clinic itself. There is no outside help desk and nobody else can see your records.',
    'shell.helpLogins': 'Logins, roles and a forgotten password are handled by your clinic admin from the staff screen.',
    'shell.helpBackups': 'Backups, restores and the audit log also sit with the admin, on the administration screen.',
    'shell.faq1Q': 'Why can I not edit a signed prescription?',
    'shell.faq1A': 'Signing closes the prescription for that visit, and the server refuses an edit outright. Record what changed as an addendum, or reopen the prescription and sign it again - the reopen itself is written into the addenda so the timeline shows it happened.',
    'shell.faq2Q': 'Why does a discount need a reason?',
    'shell.faq2A': 'Any discount above zero is rejected without a reason, and it can never exceed the fee. The reason is stored with the payment, so a fee that looks wrong in next month’s day book can still be explained.',
    'shell.faq3Q': 'Why does booking past a full session only warn me?',
    'shell.faq3A': 'A session can carry a maximum patient count, but clinics fit people in and the software is not there to argue. The extra booking is appended to that session and flagged as overflow, so it is visible without being blocked.',
    'shell.faq4Q': 'How does the follow-up fee window work?',
    'shell.faq4A': 'Each doctor has a follow-up window in days. A patient that doctor has seen before, coming back inside the window, is typed as a follow-up and charged the follow-up fee; outside it the visit becomes a review at the full consultation fee. A patient that doctor has never seen is always new.',
    'shell.faq5Q': 'Why are vitals added instead of corrected?',
    'shell.faq5A': 'Every reading is saved as its own row with its own timestamp. A morning blood pressure and an evening one are both true, so the visit lists them in order rather than letting the second erase the first.',
    'shell.faq6Q': 'What does the token number mean?',
    'shell.faq6A': 'Tokens count visits per doctor per day and restart each morning. Token 4 is the fourth visit opened for that doctor today, not the fourth patient in the building, so two doctors can each be calling a token 4.',
    'shell.faq7Q': 'How do I correct a payment?',
    'shell.faq7A': 'A visit that is already paid refuses further edits to the payment. Record the refund or the correction as an adjustment: the original row stays exactly as it was and the adjustment sits beside it, so the day book still totals correctly.',
    'shell.faq8Q': 'What happens when a staff login is deactivated?',
    'shell.faq8A': 'Sign-in answers that the account has been deactivated, and nothing else changes - their past visits, prescriptions and audit entries stay attributed to them. An admin cannot deactivate their own account, so the clinic cannot lock itself out.',
    'shell.faq9Q': 'Why is the allergy banner red on every screen?',
    'shell.faq9A': 'Allergies are held on the patient and repeated on the queue row, the visit, the appointment card and the printed letterhead. It is the one field allowed to shout, because a missed allergy is the most expensive mistake this system can help avoid.',
    'shell.faq10Q': 'How do backups and restore work?',
    'shell.faq10A': 'An admin takes a backup from the admin screen; the file is written into the clinic’s backups folder and is only reported as a success once the bytes are actually on disk. A restore replaces the current database from the file you pick, and both actions are written to the audit log.',

    // search
    'shell.searchFailed': 'Search failed.',
    'shell.searchAria': 'Search patients, doctors and records',
    'shell.searchPh': 'Patient, doctor or diagnosis',
    'shell.clear': 'Clear',
    'shell.records': 'Records',
    'shell.recent': 'Recent',
    'shell.nothingSearched': 'Nothing searched yet',
    'shell.nothingSearchedBody': 'Two letters is enough to start. Families share a mobile number, so the name is often the faster way in.',
    'shell.noMatchFor': 'No match for “{term}”',
    'shell.noMatchHint': 'Try fewer letters, or search the other tabs.',
    'shell.allergyLine': 'Allergy: {allergies}',
    'shell.visitCount_one': '{n} visit',
    'shell.visitCount_other': '{n} visits',
    /** The words behind SPECIALTY_FALLBACK, for screens that render it. */
    'shell.specialtyFallback': 'General practice',
    /** greetingName() for an account with no name: "Hi, there". */
    'shell.greetingFallback': 'there',

    // dates and queue position
    'shell.dayAtTime': '{day} at {time}',
    'shell.serialN': 'Serial #{n}',
    'shell.serialOfTotal': 'Serial #{n} of {total}',
    'shell.serialWithDoctor': 'Serial #{n} · with the doctor now',
    'shell.serialNextAfter': 'Serial #{n} · next in line after #{now}',
    'shell.serialNext': 'Serial #{n} · next in line',
    'shell.serialNowServing': 'Serial #{n} · now serving #{now}',
    'shell.serialNextUp': 'Serial #{n} · next up #{next}',
    'shell.serialAria': 'Serial {n}',
    'shell.serialAriaActive': 'Serial {n}, with the doctor now',
    'shell.serialAriaDone': 'Serial {n}, completed',

    // appointment card
    'shell.missed': 'Missed',
    'shell.nowServing': 'Now serving',
    'shell.bookingDetails': 'Booking details',
    'shell.consultation': 'Consultation',
    'shell.clinic': 'Clinic',
    'shell.payNow': 'Pay now',
    'shell.missedDesk': 'This day has passed without a check-in. Mark it a no-show, move it, or cancel it.',
    'shell.missedPatient': 'This date has passed. Book a new appointment if you still need to be seen.',
    'shell.markNoShow': 'Mark no-show',
    'shell.checkIn': 'Check in',

    // navigation, sliders, sharing, pickers
    'shell.slideOf': 'Show {i} of {count}',
    'shell.prevDay': 'Previous day',
    'shell.nextDay': 'Next day',
    'shell.shareOnWhatsApp': 'Share on WhatsApp',
    'shell.copied': 'Copied. Paste it into any app.',
    'shell.copyFailed': 'Could not copy on this device.',
    'shell.shareTitle': 'Share {title}',
    'shell.textMessage': 'Text message',
    'shell.copyText': 'Copy text',
    'shell.nMore': '{n} more',
    'shell.searchPatients': 'Search patients',
    'shell.patientSearchPh': 'Name, mobile or patient code',
    'shell.noPatientMatch': 'No patient matches “{q}”.',
    'shell.prevDoctor': 'Previous doctor',
    'shell.nextDoctor': 'Next doctor',
  },
  hi: {
    'shell.signInHead': '{app} में साइन इन करें',
    'shell.signInSub': 'स्टाफ़, डॉक्टर और मरीज़',
    'shell.notAvailable': 'उपलब्ध नहीं',
    'shell.noAccessTitle': 'आपकी भूमिका को इस स्क्रीन की अनुमति नहीं है।',
    'shell.noAccessBody': 'अगर आपको लगता है कि यह अनुमति मिलनी चाहिए, तो एडमिन से पूछें।',
    'shell.loadingScreen': 'स्क्रीन लोड हो रही है…',
    'shell.offlineBanner': 'क्लिनिक सर्वर से संपर्क नहीं हो पा रहा — {time} बजे सेव की गई जानकारी दिखाई जा रही है। बदलाव करने के लिए कनेक्शन वापस आना ज़रूरी है।',

    'shell.sections': 'मुख्य भाग',
    'shell.tabHome': 'होम',
    'shell.tabMyVisits': 'मेरी विज़िट',
    'shell.tabBookings': 'बुकिंग',
    'shell.tabMyRecord': 'मेरा रिकॉर्ड',
    'shell.tabProfile': 'प्रोफ़ाइल',

    'shell.w1Title': 'क्लिनिक आपकी जेब में',
    'shell.w1Lead': 'डेस्कटॉप की हर स्क्रीन यहाँ भी है। वही लॉगिन, वही रिकॉर्ड, वही नियम — बस मरीज़ों के बीच एक हाथ से चलाने लायक।',
    'shell.w1P1': 'कुछ भी दोहरी कॉपी नहीं: फ़ोन और डेस्क एक ही API से जुड़े हैं',
    'shell.w1P2': 'आप क्या खोल सकते हैं, यह आपकी भूमिका पर निर्भर है — बिल्कुल डेस्क की तरह',
    'shell.w1P3': 'डेस्कटॉप ऐप आपकी प्रोफ़ाइल से हमेशा एक टैप दूर है',
    'shell.w2Title': 'कतार से पर्चे तक',
    'shell.w2Lead': 'रिसेप्शन विज़िट खोलता है और मरीज़ को उस दिन, उस डॉक्टर का टोकन मिलता है। वहाँ से कंसल्टिंग रूम कतार संभालता है।',
    'shell.w2P1': 'हर सुबह हर डॉक्टर के टोकन फिर से शुरू होते हैं, इसलिए दो डॉक्टर एक साथ टोकन 4 पर हो सकते हैं',
    'shell.w2P2': 'वाइटल्स जोड़े जाते हैं, कभी मिटाए नहीं जाते — हर रीडिंग का समय दर्ज रहता है',
    'shell.w2P3': 'प्रिस्क्रिप्शन पर साइन करते ही वह बंद हो जाता है; सुधार अलग नोट (एडेंडम) के रूप में जोड़े जाते हैं',
    'shell.w3Title': 'रिकॉर्ड, जिनका हिसाब हमेशा मिलता है',
    'shell.w3Lead': 'पिछली विज़िट, सलाह दिए गए टेस्ट और रोज़ का हिसाब — सब यहाँ है, और आपको दिखने वाली हर राशि किसी की दर्ज की गई एंट्री से जुड़ी है।',
    'shell.w3P1': 'लैब टेस्ट की बुकिंग, भुगतान और ट्रैकिंग लैब स्क्रीन से होती है',
    'shell.w3P2': 'छूट के लिए कारण ज़रूरी है, और भुगतान हो चुकी विज़िट में सुधार एडजस्टमेंट से होता है',
    'shell.w3P3': 'एलर्जी की जानकारी मरीज़ के हर कार्ड और हर प्रिंटआउट पर दिखती है',
    'shell.goToStep': 'चरण {n} पर जाएँ',
    'shell.skip': 'छोड़ें',
    'shell.getStarted': 'शुरू करें',

    'shell.enterPin': 'अपना PIN डालें',
    'shell.stillSignedIn': '{name} इस डिवाइस पर अभी भी साइन इन हैं।',
    'shell.deviceLocked': 'यह डिवाइस लॉक है।',
    'shell.pinEntry': 'PIN डालें',
    'shell.pinWrong': 'यह PIN सही नहीं है। फिर से कोशिश करें।',
    'shell.fourDigits': 'चार अंक।',
    'shell.deleteDigit': 'आख़िरी अंक मिटाएँ',
    'shell.signOutInstead': 'इसके बजाय साइन आउट करें',
    'shell.forgotPin': 'PIN भूल गए? साइन आउट करने से यह हट जाता है, और क्लिनिक पासवर्ड से दोबारा साइन इन करके आप नया PIN बना सकते हैं।',

    'shell.couldNotSignIn': 'साइन इन नहीं हो सका।',
    'shell.welcomeBack': 'फिर से स्वागत है',
    'shell.signInTo': '{clinic} में साइन इन करें।',
    'shell.passwordCleared': 'पासवर्ड बॉक्स खाली कर दिया गया है। अगर आपके ब्राउज़र ने इसे अपने आप भरा था, तो वह सेव किया हुआ पासवर्ड पुराना है — मौजूदा पासवर्ड टाइप करें।',
    'shell.username': 'यूज़रनेम',
    'shell.password': 'पासवर्ड',
    'shell.hidePassword': 'पासवर्ड छिपाएँ',
    'shell.showPassword': 'पासवर्ड दिखाएँ',
    'shell.signingIn': 'साइन इन हो रहा है…',
    'shell.signIn': 'साइन इन करें',
    'shell.accountsByAdmin': 'खाते क्लिनिक एडमिन बनाते हैं। अगर आप लॉक आउट हो गए हैं, तो बार-बार कोशिश करने के बजाय उनसे पासवर्ड रीसेट करवाएँ।',

    'shell.help': 'सहायता',
    'shell.faq': 'सामान्य सवाल',
    'shell.contact': 'संपर्क',
    'shell.howSystemBehaves': 'यह सिस्टम कैसे काम करता है',
    'shell.thisClinic': 'यह क्लिनिक',
    'shell.phone': 'फ़ोन',
    'shell.alternate': 'दूसरा नंबर',
    'shell.opdHours': 'OPD समय',
    'shell.whoToAsk': 'किससे पूछें',
    'shell.helpRunByClinic': 'यह सिस्टम क्लिनिक ख़ुद चलाता है। कोई बाहरी हेल्प डेस्क नहीं है और आपके रिकॉर्ड कोई और नहीं देख सकता।',
    'shell.helpLogins': 'लॉगिन, भूमिकाएँ और भूला हुआ पासवर्ड — ये सब आपके क्लिनिक एडमिन स्टाफ़ स्क्रीन से संभालते हैं।',
    'shell.helpBackups': 'बैकअप, रीस्टोर और ऑडिट लॉग भी एडमिन के पास हैं, एडमिन स्क्रीन पर।',
    'shell.faq1Q': 'साइन किए गए प्रिस्क्रिप्शन में बदलाव क्यों नहीं हो सकता?',
    'shell.faq1A': 'साइन करते ही उस विज़िट का प्रिस्क्रिप्शन बंद हो जाता है, और सर्वर कोई भी बदलाव सीधे मना कर देता है। जो बदला है उसे एडेंडम (अतिरिक्त नोट) के रूप में दर्ज करें, या प्रिस्क्रिप्शन दोबारा खोलकर फिर से साइन करें — दोबारा खोलना भी एडेंडम में लिखा जाता है, ताकि टाइमलाइन में दिखे कि ऐसा हुआ था।',
    'shell.faq2Q': 'छूट के लिए कारण क्यों ज़रूरी है?',
    'shell.faq2A': 'शून्य से ज़्यादा कोई भी छूट बिना कारण के स्वीकार नहीं होती, और छूट कभी फ़ीस से ज़्यादा नहीं हो सकती। कारण भुगतान के साथ सेव होता है, ताकि अगले महीने के हिसाब में कोई फ़ीस गलत लगे तो भी उसकी वजह बताई जा सके।',
    'shell.faq3Q': 'भरे हुए सेशन में बुकिंग करने पर सिर्फ़ चेतावनी क्यों मिलती है?',
    'shell.faq3A': 'किसी सेशन में मरीज़ों की अधिकतम संख्या तय हो सकती है, पर क्लिनिक ज़रूरत पड़ने पर लोगों को देख ही लेते हैं, और सॉफ़्टवेयर इसमें अड़चन नहीं बनता। अतिरिक्त बुकिंग उसी सेशन में जुड़ जाती है और "ओवरफ़्लो" के रूप में चिह्नित होती है, ताकि वह रुके बिना दिखती रहे।',
    'shell.faq4Q': 'फ़ॉलो-अप फ़ीस की समय-सीमा कैसे काम करती है?',
    'shell.faq4A': 'हर डॉक्टर की फ़ॉलो-अप की एक समय-सीमा (दिनों में) होती है। जिस मरीज़ को वह डॉक्टर पहले देख चुके हैं, अगर वह इस सीमा के अंदर वापस आता है, तो विज़िट फ़ॉलो-अप मानी जाती है और फ़ॉलो-अप फ़ीस लगती है; सीमा के बाद वह रिव्यू बन जाती है और पूरी परामर्श फ़ीस लगती है। जिस मरीज़ को उस डॉक्टर ने कभी नहीं देखा, वह हमेशा नया होता है।',
    'shell.faq5Q': 'वाइटल्स सुधारे जाने के बजाय जोड़े क्यों जाते हैं?',
    'shell.faq5A': 'हर रीडिंग अपने समय के साथ अलग से सेव होती है। सुबह का BP और शाम का — दोनों सही हैं, इसलिए विज़िट में दोनों क्रम से दिखते हैं, दूसरा पहले को मिटाता नहीं।',
    'shell.faq6Q': 'टोकन नंबर का क्या मतलब है?',
    'shell.faq6A': 'टोकन हर डॉक्टर की हर दिन की विज़िट गिनते हैं और हर सुबह फिर से शुरू होते हैं। टोकन 4 का मतलब है आज उस डॉक्टर के लिए खोली गई चौथी विज़िट, न कि पूरे क्लिनिक का चौथा मरीज़ — इसलिए दो डॉक्टर एक साथ टोकन 4 बुला सकते हैं।',
    'shell.faq7Q': 'भुगतान में सुधार कैसे करें?',
    'shell.faq7A': 'जिस विज़िट का भुगतान हो चुका है, उसके भुगतान में आगे कोई बदलाव नहीं होता। रिफ़ंड या सुधार को एडजस्टमेंट के रूप में दर्ज करें: मूल एंट्री जैसी थी वैसी ही रहती है और एडजस्टमेंट उसके साथ जुड़ जाता है, ताकि दिन का हिसाब सही बना रहे।',
    'shell.faq8Q': 'स्टाफ़ का लॉगिन बंद करने पर क्या होता है?',
    'shell.faq8A': 'साइन इन करने पर बताया जाता है कि खाता बंद कर दिया गया है, और कुछ नहीं बदलता — उनकी पिछली विज़िट, प्रिस्क्रिप्शन और ऑडिट एंट्री उन्हीं के नाम रहती हैं। एडमिन अपना ख़ुद का खाता बंद नहीं कर सकते, ताकि क्लिनिक ख़ुद को बाहर लॉक न कर ले।',
    'shell.faq9Q': 'एलर्जी की पट्टी हर स्क्रीन पर लाल क्यों होती है?',
    'shell.faq9A': 'एलर्जी मरीज़ की जानकारी में रहती है और कतार, विज़िट, अपॉइंटमेंट कार्ड और छपे हुए लेटरहेड पर दोहराई जाती है। यही एक जानकारी है जिसे इतना उभारकर दिखाया जाता है, क्योंकि छूटी हुई एलर्जी सबसे महंगी गलती है जिसे रोकने में यह सिस्टम मदद कर सकता है।',
    'shell.faq10Q': 'बैकअप और रीस्टोर कैसे काम करते हैं?',
    'shell.faq10A': 'एडमिन, एडमिन स्क्रीन से बैकअप लेते हैं; फ़ाइल क्लिनिक के बैकअप फ़ोल्डर में लिखी जाती है और तभी सफल बताई जाती है जब वह सच में डिस्क पर सेव हो जाए। रीस्टोर आपकी चुनी हुई फ़ाइल से मौजूदा डेटाबेस को बदल देता है, और दोनों काम ऑडिट लॉग में दर्ज होते हैं।',

    'shell.searchFailed': 'खोज नहीं हो सकी।',
    'shell.searchAria': 'मरीज़, डॉक्टर और रिकॉर्ड खोजें',
    'shell.searchPh': 'मरीज़, डॉक्टर या निदान',
    'shell.clear': 'साफ़ करें',
    'shell.records': 'रिकॉर्ड',
    'shell.recent': 'हाल की खोजें',
    'shell.nothingSearched': 'अभी तक कुछ नहीं खोजा गया',
    'shell.nothingSearchedBody': 'शुरू करने के लिए दो अक्षर काफ़ी हैं। परिवारों का मोबाइल नंबर अक्सर एक ही होता है, इसलिए नाम से खोजना अक्सर जल्दी होता है।',
    'shell.noMatchFor': '“{term}” से कुछ नहीं मिला',
    'shell.noMatchHint': 'कम अक्षर डालकर देखें, या दूसरे टैब में खोजें।',
    'shell.allergyLine': 'एलर्जी: {allergies}',
    'shell.visitCount_one': '{n} विज़िट',
    'shell.visitCount_other': '{n} विज़िट',
    'shell.specialtyFallback': 'सामान्य चिकित्सा',
    'shell.greetingFallback': 'जी',

    'shell.dayAtTime': '{day}, {time}',
    'shell.serialN': 'क्रमांक #{n}',
    'shell.serialOfTotal': 'क्रमांक #{n} ({total} में से)',
    'shell.serialWithDoctor': 'क्रमांक #{n} · अभी डॉक्टर के पास',
    'shell.serialNextAfter': 'क्रमांक #{n} · #{now} के बाद अगला नंबर',
    'shell.serialNext': 'क्रमांक #{n} · अगला नंबर',
    'shell.serialNowServing': 'क्रमांक #{n} · अभी #{now} चल रहा है',
    'shell.serialNextUp': 'क्रमांक #{n} · अगला #{next}',
    'shell.serialAria': 'क्रमांक {n}',
    'shell.serialAriaActive': 'क्रमांक {n}, अभी डॉक्टर के पास',
    'shell.serialAriaDone': 'क्रमांक {n}, पूरा हुआ',

    'shell.missed': 'छूट गई',
    'shell.nowServing': 'अभी चल रहा',
    'shell.bookingDetails': 'बुकिंग की जानकारी',
    'shell.consultation': 'परामर्श',
    'shell.clinic': 'क्लिनिक',
    'shell.payNow': 'अभी भुगतान करें',
    'shell.missedDesk': 'यह दिन बिना चेक-इन के निकल गया। इसे "नहीं आए" दर्ज करें, समय बदलें या रद्द करें।',
    'shell.missedPatient': 'यह तारीख निकल चुकी है। अगर अब भी दिखाना है, तो नई अपॉइंटमेंट बुक करें।',
    'shell.markNoShow': '"नहीं आए" दर्ज करें',
    'shell.checkIn': 'चेक-इन करें',

    'shell.slideOf': '{count} में से {i} दिखाएँ',
    'shell.prevDay': 'पिछला दिन',
    'shell.nextDay': 'अगला दिन',
    'shell.shareOnWhatsApp': 'WhatsApp पर भेजें',
    'shell.copied': 'कॉपी हो गया। इसे किसी भी ऐप में पेस्ट करें।',
    'shell.copyFailed': 'इस डिवाइस पर कॉपी नहीं हो सका।',
    'shell.shareTitle': '{title} भेजें',
    'shell.textMessage': 'मैसेज (SMS)',
    'shell.copyText': 'टेक्स्ट कॉपी करें',
    'shell.nMore': '{n} और',
    'shell.searchPatients': 'मरीज़ खोजें',
    'shell.patientSearchPh': 'नाम, मोबाइल या मरीज़ कोड',
    'shell.noPatientMatch': '“{q}” से मेल खाता कोई मरीज़ नहीं मिला।',
    'shell.prevDoctor': 'पिछले डॉक्टर',
    'shell.nextDoctor': 'अगले डॉक्टर',
  },
  mr: {
    'shell.signInHead': '{app} मध्ये साइन इन करा',
    'shell.signInSub': 'स्टाफ, डॉक्टर आणि रुग्ण',
    'shell.notAvailable': 'उपलब्ध नाही',
    'shell.noAccessTitle': 'तुमच्या भूमिकेला ही स्क्रीन उघडण्याची परवानगी नाही.',
    'shell.noAccessBody': 'ही परवानगी हवी असे वाटत असल्यास ॲडमिनला विचारा.',
    'shell.loadingScreen': 'स्क्रीन लोड होत आहे…',
    'shell.offlineBanner': 'क्लिनिकच्या सर्व्हरशी संपर्क होत नाही — {time} वाजता सेव्ह केलेली माहिती दाखवत आहोत. बदल करण्यासाठी कनेक्शन परत यायला हवे.',

    'shell.sections': 'विभाग',
    'shell.tabHome': 'होम',
    'shell.tabMyVisits': 'माझ्या भेटी',
    'shell.tabBookings': 'बुकिंग',
    'shell.tabMyRecord': 'माझे रेकॉर्ड',
    'shell.tabProfile': 'प्रोफाइल',

    'shell.w1Title': 'क्लिनिक तुमच्या खिशात',
    'shell.w1Lead': 'डेस्कटॉपवरील प्रत्येक स्क्रीन इथेही आहे. तेच लॉगिन, तेच रेकॉर्ड, तेच नियम — फक्त रुग्णांच्या मधल्या वेळेत एका हाताने वापरता येईल असे.',
    'shell.w1P1': 'काहीही दुसरी प्रत नाही: फोन आणि डेस्क एकाच API शी जोडलेले आहेत',
    'shell.w1P2': 'तुम्ही काय उघडू शकता हे तुमच्या भूमिकेवर ठरते — अगदी डेस्कप्रमाणेच',
    'shell.w1P3': 'डेस्कटॉप ॲप तुमच्या प्रोफाइलमधून नेहमी एका टॅपवर आहे',
    'shell.w2Title': 'रांगेपासून प्रिस्क्रिप्शनपर्यंत',
    'shell.w2Lead': 'रिसेप्शन भेट सुरू करते आणि रुग्णाला त्या दिवशीचा, त्या डॉक्टरांचा टोकन मिळतो. तिथून पुढे तपासणी कक्ष रांग सांभाळतो.',
    'shell.w2P1': 'दर सकाळी प्रत्येक डॉक्टरचे टोकन पुन्हा सुरू होतात, त्यामुळे दोन डॉक्टर एकाच वेळी टोकन 4 वर असू शकतात',
    'shell.w2P2': 'व्हायटल्स जोडले जातात, कधीही पुसले जात नाहीत — प्रत्येक नोंदीची वेळ राहते',
    'shell.w2P3': 'प्रिस्क्रिप्शनवर सही केल्यावर ते बंद होते; दुरुस्ती वेगळी नोंद (ॲडेंडम) म्हणून जोडली जाते',
    'shell.w3Title': 'हिशोब नेहमी जुळणारे रेकॉर्ड',
    'shell.w3Lead': 'मागील भेटी, सुचवलेल्या तपासण्या आणि रोजचा हिशोब — सर्व इथे आहे, आणि दिसणारी प्रत्येक रक्कम कोणीतरी केलेल्या नोंदीशी जोडलेली आहे.',
    'shell.w3P1': 'लॅब तपासण्यांचे बुकिंग, पेमेंट आणि ट्रॅकिंग लॅब स्क्रीनवरून होते',
    'shell.w3P2': 'सवलतीसाठी कारण लागते, आणि पैसे भरलेल्या भेटीतील दुरुस्ती ॲडजस्टमेंटने होते',
    'shell.w3P3': 'ॲलर्जीची माहिती रुग्णाच्या प्रत्येक कार्डवर आणि प्रत्येक प्रिंटआउटवर दिसते',
    'shell.goToStep': 'पायरी {n} वर जा',
    'shell.skip': 'वगळा',
    'shell.getStarted': 'सुरू करा',

    'shell.enterPin': 'तुमचा PIN टाका',
    'shell.stillSignedIn': '{name} या डिव्हाइसवर अजूनही साइन इन आहेत.',
    'shell.deviceLocked': 'हे डिव्हाइस लॉक आहे.',
    'shell.pinEntry': 'PIN नोंद',
    'shell.pinWrong': 'हा PIN चुकीचा आहे. पुन्हा प्रयत्न करा.',
    'shell.fourDigits': 'चार अंक.',
    'shell.deleteDigit': 'शेवटचा अंक पुसा',
    'shell.signOutInstead': 'त्याऐवजी साइन आउट करा',
    'shell.forgotPin': 'PIN विसरलात? साइन आउट केल्यावर तो पुसला जातो, आणि क्लिनिकच्या पासवर्डने पुन्हा साइन इन करून तुम्ही नवीन PIN ठेवू शकता.',

    'shell.couldNotSignIn': 'साइन इन होऊ शकले नाही.',
    'shell.welcomeBack': 'पुन्हा स्वागत आहे',
    'shell.signInTo': '{clinic} मध्ये साइन इन करा.',
    'shell.passwordCleared': 'पासवर्ड बॉक्स रिकामा केला आहे. तुमच्या ब्राउझरने तो आपोआप भरला असेल, तर तो सेव्ह केलेला पासवर्ड जुना आहे — सध्याचा पासवर्ड टाइप करा.',
    'shell.username': 'युजरनेम',
    'shell.password': 'पासवर्ड',
    'shell.hidePassword': 'पासवर्ड लपवा',
    'shell.showPassword': 'पासवर्ड दाखवा',
    'shell.signingIn': 'साइन इन होत आहे…',
    'shell.signIn': 'साइन इन करा',
    'shell.accountsByAdmin': 'खाती क्लिनिकचे ॲडमिन तयार करतात. तुम्ही लॉक आउट झाला असाल, तर पुन्हा पुन्हा प्रयत्न करण्याऐवजी त्यांच्याकडून पासवर्ड रीसेट करून घ्या.',

    'shell.help': 'मदत',
    'shell.faq': 'नेहमीचे प्रश्न',
    'shell.contact': 'संपर्क',
    'shell.howSystemBehaves': 'ही प्रणाली कशी काम करते',
    'shell.thisClinic': 'हे क्लिनिक',
    'shell.phone': 'फोन',
    'shell.alternate': 'पर्यायी नंबर',
    'shell.opdHours': 'OPD वेळ',
    'shell.whoToAsk': 'कोणाला विचारावे',
    'shell.helpRunByClinic': 'ही प्रणाली क्लिनिक स्वतः चालवते. बाहेरचा कोणताही हेल्प डेस्क नाही आणि तुमचे रेकॉर्ड इतर कोणीही पाहू शकत नाही.',
    'shell.helpLogins': 'लॉगिन, भूमिका आणि विसरलेला पासवर्ड — हे सर्व तुमचे क्लिनिक ॲडमिन स्टाफ स्क्रीनवरून हाताळतात.',
    'shell.helpBackups': 'बॅकअप, रिस्टोअर आणि ऑडिट लॉगही ॲडमिनकडेच, ॲडमिन स्क्रीनवर असतात.',
    'shell.faq1Q': 'सही केलेले प्रिस्क्रिप्शन का बदलता येत नाही?',
    'shell.faq1A': 'सही केल्यावर त्या भेटीचे प्रिस्क्रिप्शन बंद होते, आणि सर्व्हर कोणताही बदल सरळ नाकारतो. जो बदल झाला तो ॲडेंडम (अतिरिक्त नोंद) म्हणून नोंदवा, किंवा प्रिस्क्रिप्शन पुन्हा उघडून पुन्हा सही करा — पुन्हा उघडल्याचीही नोंद ॲडेंडममध्ये होते, त्यामुळे टाइमलाइनमध्ये ते दिसते.',
    'shell.faq2Q': 'सवलतीसाठी कारण का लागते?',
    'shell.faq2A': 'शून्यापेक्षा जास्त कोणतीही सवलत कारणाशिवाय स्वीकारली जात नाही, आणि ती कधीही फीपेक्षा जास्त असू शकत नाही. कारण पेमेंटसोबत सेव्ह होते, त्यामुळे पुढच्या महिन्याच्या हिशोबात एखादी फी चुकीची वाटली तरी तिचे स्पष्टीकरण देता येते.',
    'shell.faq3Q': 'भरलेल्या सत्रात बुकिंग केल्यावर फक्त इशारा का मिळतो?',
    'shell.faq3A': 'एखाद्या सत्रात रुग्णांची कमाल संख्या ठरलेली असू शकते, पण गरज पडल्यास क्लिनिक रुग्णांना पाहतातच, आणि सॉफ्टवेअर त्यात अडथळा आणत नाही. जादा बुकिंग त्याच सत्रात जोडली जाते आणि "ओव्हरफ्लो" म्हणून खूण केली जाते, त्यामुळे ती न अडवता दिसते.',
    'shell.faq4Q': 'फॉलो-अप फीची मुदत कशी काम करते?',
    'shell.faq4A': 'प्रत्येक डॉक्टरची फॉलो-अपची एक मुदत (दिवसांत) असते. त्या डॉक्टरांनी आधी पाहिलेला रुग्ण या मुदतीत परत आला, तर भेट फॉलो-अप मानली जाते आणि फॉलो-अप फी लागते; मुदतीनंतर ती रिव्ह्यू होते आणि पूर्ण तपासणी फी लागते. त्या डॉक्टरांनी कधीच न पाहिलेला रुग्ण नेहमी नवीन असतो.',
    'shell.faq5Q': 'व्हायटल्स दुरुस्त करण्याऐवजी नवीन का जोडले जातात?',
    'shell.faq5A': 'प्रत्येक नोंद तिच्या वेळेसह वेगळी सेव्ह होते. सकाळचा BP आणि संध्याकाळचा — दोन्ही खरे आहेत, म्हणून भेटीत दोन्ही क्रमाने दिसतात; दुसरी नोंद पहिलीला पुसत नाही.',
    'shell.faq6Q': 'टोकन नंबरचा अर्थ काय?',
    'shell.faq6A': 'टोकन प्रत्येक डॉक्टरच्या दररोजच्या भेटी मोजतात आणि दर सकाळी पुन्हा सुरू होतात. टोकन 4 म्हणजे आज त्या डॉक्टरसाठी उघडलेली चौथी भेट, संपूर्ण क्लिनिकमधला चौथा रुग्ण नव्हे — म्हणून दोन डॉक्टर एकाच वेळी टोकन 4 पुकारू शकतात.',
    'shell.faq7Q': 'पेमेंट कसे दुरुस्त करावे?',
    'shell.faq7A': 'पैसे भरलेल्या भेटीच्या पेमेंटमध्ये नंतर बदल करता येत नाही. परतावा किंवा दुरुस्ती ॲडजस्टमेंट म्हणून नोंदवा: मूळ नोंद जशी होती तशीच राहते आणि ॲडजस्टमेंट तिच्या शेजारी जोडले जाते, त्यामुळे दिवसाचा हिशोब बरोबर राहतो.',
    'shell.faq8Q': 'स्टाफचे लॉगिन बंद केल्यावर काय होते?',
    'shell.faq8A': 'साइन इन करताना खाते बंद केल्याचे सांगितले जाते, आणि बाकी काहीही बदलत नाही — त्यांच्या मागील भेटी, प्रिस्क्रिप्शन आणि ऑडिट नोंदी त्यांच्याच नावावर राहतात. ॲडमिन स्वतःचे खाते बंद करू शकत नाहीत, त्यामुळे क्लिनिक स्वतःलाच लॉक आउट करू शकत नाही.',
    'shell.faq9Q': 'ॲलर्जीची पट्टी प्रत्येक स्क्रीनवर लाल का असते?',
    'shell.faq9A': 'ॲलर्जी रुग्णाच्या माहितीत असते आणि रांग, भेट, अपॉइंटमेंट कार्ड आणि छापील लेटरहेडवर पुन्हा दाखवली जाते. हीच एक माहिती ठळकपणे दाखवली जाते, कारण नजरेतून सुटलेली ॲलर्जी ही सर्वात महागडी चूक आहे, जी टाळायला ही प्रणाली मदत करू शकते.',
    'shell.faq10Q': 'बॅकअप आणि रिस्टोअर कसे काम करतात?',
    'shell.faq10A': 'ॲडमिन, ॲडमिन स्क्रीनवरून बॅकअप घेतात; फाइल क्लिनिकच्या बॅकअप फोल्डरमध्ये लिहिली जाते आणि ती प्रत्यक्ष डिस्कवर सेव्ह झाल्यावरच यशस्वी दाखवली जाते. रिस्टोअर तुम्ही निवडलेल्या फाइलमधून सध्याचा डेटाबेस बदलतो, आणि दोन्ही क्रिया ऑडिट लॉगमध्ये नोंदवल्या जातात.',

    'shell.searchFailed': 'शोध होऊ शकला नाही.',
    'shell.searchAria': 'रुग्ण, डॉक्टर आणि रेकॉर्ड शोधा',
    'shell.searchPh': 'रुग्ण, डॉक्टर किंवा निदान',
    'shell.clear': 'पुसा',
    'shell.records': 'रेकॉर्ड',
    'shell.recent': 'अलीकडील शोध',
    'shell.nothingSearched': 'अजून काहीही शोधलेले नाही',
    'shell.nothingSearchedBody': 'सुरुवातीला दोन अक्षरे पुरेशी आहेत. कुटुंबांचा मोबाइल नंबर बहुधा एकच असतो, म्हणून नावाने शोधणे अनेकदा लवकर होते.',
    'shell.noMatchFor': '“{term}” साठी काहीही सापडले नाही',
    'shell.noMatchHint': 'कमी अक्षरे वापरून पाहा, किंवा इतर टॅबमध्ये शोधा.',
    'shell.allergyLine': 'ॲलर्जी: {allergies}',
    'shell.visitCount_one': '{n} भेट',
    'shell.visitCount_other': '{n} भेटी',
    'shell.specialtyFallback': 'सामान्य उपचार',
    'shell.greetingFallback': 'जी',

    'shell.dayAtTime': '{day}, {time}',
    'shell.serialN': 'क्रमांक #{n}',
    'shell.serialOfTotal': 'क्रमांक #{n} ({total} पैकी)',
    'shell.serialWithDoctor': 'क्रमांक #{n} · आता डॉक्टरांकडे',
    'shell.serialNextAfter': 'क्रमांक #{n} · #{now} नंतर पुढचा नंबर',
    'shell.serialNext': 'क्रमांक #{n} · पुढचा नंबर',
    'shell.serialNowServing': 'क्रमांक #{n} · सध्या #{now} चालू',
    'shell.serialNextUp': 'क्रमांक #{n} · पुढचा #{next}',
    'shell.serialAria': 'क्रमांक {n}',
    'shell.serialAriaActive': 'क्रमांक {n}, आता डॉक्टरांकडे',
    'shell.serialAriaDone': 'क्रमांक {n}, पूर्ण',

    'shell.missed': 'चुकली',
    'shell.nowServing': 'सध्या चालू',
    'shell.bookingDetails': 'बुकिंगची माहिती',
    'shell.consultation': 'तपासणी',
    'shell.clinic': 'क्लिनिक',
    'shell.payNow': 'आता पैसे भरा',
    'shell.missedDesk': 'हा दिवस चेक-इनशिवाय गेला. याला "आले नाहीत" म्हणून नोंदवा, वेळ बदला किंवा रद्द करा.',
    'shell.missedPatient': 'ही तारीख उलटून गेली आहे. अजूनही तपासणी हवी असल्यास नवीन अपॉइंटमेंट बुक करा.',
    'shell.markNoShow': '"आले नाहीत" नोंदवा',
    'shell.checkIn': 'चेक-इन करा',

    'shell.slideOf': '{count} पैकी {i} दाखवा',
    'shell.prevDay': 'मागील दिवस',
    'shell.nextDay': 'पुढील दिवस',
    'shell.shareOnWhatsApp': 'WhatsApp वर पाठवा',
    'shell.copied': 'कॉपी झाले. कोणत्याही ॲपमध्ये पेस्ट करा.',
    'shell.copyFailed': 'या डिव्हाइसवर कॉपी करता आले नाही.',
    'shell.shareTitle': '{title} पाठवा',
    'shell.textMessage': 'मेसेज (SMS)',
    'shell.copyText': 'मजकूर कॉपी करा',
    'shell.nMore': 'आणखी {n}',
    'shell.searchPatients': 'रुग्ण शोधा',
    'shell.patientSearchPh': 'नाव, मोबाइल किंवा रुग्ण कोड',
    'shell.noPatientMatch': '“{q}” शी जुळणारा रुग्ण सापडला नाही.',
    'shell.prevDoctor': 'मागील डॉक्टर',
    'shell.nextDoctor': 'पुढील डॉक्टर',
  },
  or: {
    'shell.signInHead': '{app} ରେ ସାଇନ୍ ଇନ୍',
    'shell.signInSub': 'କର୍ମଚାରୀ, ଡାକ୍ତର ଓ ରୋଗୀ',
    'shell.notAvailable': 'ଉପଲବ୍ଧ ନାହିଁ',
    'shell.noAccessTitle': 'ଆପଣଙ୍କ ଭୂମିକାକୁ ଏହି ସ୍କ୍ରିନ୍ ଦେଖିବାର ଅନୁମତି ନାହିଁ।',
    'shell.noAccessBody': 'ଏହି ଅନୁମତି ଦରକାର ବୋଲି ମନେହେଲେ ଆଡମିନ୍‌ଙ୍କୁ ପଚାରନ୍ତୁ।',
    'shell.loadingScreen': 'ସ୍କ୍ରିନ୍ ଲୋଡ୍ ହେଉଛି…',
    'shell.offlineBanner': 'କ୍ଲିନିକ୍ ସର୍ଭର ସହ ସଂଯୋଗ ହେଉନାହିଁ — {time} ରେ ସେଭ୍ ହୋଇଥିବା ସୂଚନା ଦେଖାଯାଉଛି। ପରିବର୍ତ୍ତନ ପାଇଁ ସଂଯୋଗ ଫେରିବା ଦରକାର।',

    'shell.sections': 'ବିଭାଗ',
    'shell.tabHome': 'ହୋମ୍',
    'shell.tabMyVisits': 'ମୋ ପରିଦର୍ଶନ',
    'shell.tabBookings': 'ବୁକିଂ',
    'shell.tabMyRecord': 'ମୋ ରେକର୍ଡ',
    'shell.tabProfile': 'ପ୍ରୋଫାଇଲ୍',

    'shell.w1Title': 'କ୍ଲିନିକ୍ ଆପଣଙ୍କ ପକେଟରେ',
    'shell.w1Lead': 'ଡେସ୍କଟପ୍‌ର ପ୍ରତ୍ୟେକ ସ୍କ୍ରିନ୍ ଏଠାରେ ମଧ୍ୟ ଅଛି। ସେହି ଲଗଇନ୍, ସେହି ରେକର୍ଡ, ସେହି ନିୟମ — ରୋଗୀଙ୍କ ମଝିରେ ଗୋଟିଏ ହାତରେ ଚଳାଇବା ଭଳି।',
    'shell.w1P1': 'କିଛି ବି ଦ୍ୱିତୀୟ କପି ନୁହେଁ: ଫୋନ୍ ଓ ଡେସ୍କ ଗୋଟିଏ API ସହ ଯୋଡ଼ା',
    'shell.w1P2': 'ଆପଣ କ’ଣ ଖୋଲିପାରିବେ ତାହା ଆପଣଙ୍କ ଭୂମିକା ଉପରେ ନିର୍ଭର କରେ — ଠିକ୍ ଡେସ୍କ ଭଳି',
    'shell.w1P3': 'ଡେସ୍କଟପ୍ ଆପ୍ ଆପଣଙ୍କ ପ୍ରୋଫାଇଲ୍‌ରୁ ସବୁବେଳେ ଗୋଟିଏ ଟ୍ୟାପ୍ ଦୂରରେ',
    'shell.w2Title': 'ଧାଡ଼ିରୁ ପ୍ରେସକ୍ରିପସନ୍ ପର୍ଯ୍ୟନ୍ତ',
    'shell.w2Lead': 'ରିସେପସନ୍ ପରିଦର୍ଶନ ଖୋଲେ ଏବଂ ରୋଗୀ ସେହି ଦିନ ପାଇଁ ସେହି ଡାକ୍ତରଙ୍କ ଟୋକେନ୍ ପାଆନ୍ତି। ସେଠାରୁ କନସଲ୍ଟିଂ ରୁମ୍ ଧାଡ଼ି ସମ୍ଭାଳେ।',
    'shell.w2P1': 'ପ୍ରତି ସକାଳେ ପ୍ରତ୍ୟେକ ଡାକ୍ତରଙ୍କ ଟୋକେନ୍ ପୁଣି ଆରମ୍ଭ ହୁଏ, ତେଣୁ ଦୁଇ ଡାକ୍ତର ଏକାସାଙ୍ଗରେ ଟୋକେନ୍ 4 ରେ ରହିପାରନ୍ତି',
    'shell.w2P2': 'ଭାଇଟାଲ୍ସ ଯୋଡ଼ାଯାଏ, କେବେ ବି ମିଟାଯାଏ ନାହିଁ — ପ୍ରତ୍ୟେକ ରିଡିଂର ସମୟ ରହେ',
    'shell.w2P3': 'ପ୍ରେସକ୍ରିପସନ୍‌ରେ ସାଇନ୍ କଲେ ତାହା ବନ୍ଦ ହୋଇଯାଏ; ସଂଶୋଧନ ଅଲଗା ନୋଟ୍ (ଆଡେଣ୍ଡମ୍) ଭାବେ ଯୋଡ଼ାଯାଏ',
    'shell.w3Title': 'ଏପରି ରେକର୍ଡ, ଯାହାର ହିସାବ ସବୁବେଳେ ମେଳ ଖାଏ',
    'shell.w3Lead': 'ପୂର୍ବ ପରିଦର୍ଶନ, ପରାମର୍ଶିତ ପରୀକ୍ଷା ଓ ଦୈନିକ ହିସାବ — ସବୁ ଏଠାରେ ଅଛି, ଏବଂ ଆପଣ ଦେଖୁଥିବା ପ୍ରତ୍ୟେକ ରାଶି କାହାର ଲେଖିଥିବା ଏଣ୍ଟ୍ରି ସହ ଜଡ଼ିତ।',
    'shell.w3P1': 'ଲ୍ୟାବ୍ ପରୀକ୍ଷାର ବୁକିଂ, ଦେୟ ଓ ଟ୍ରାକିଂ ଲ୍ୟାବ୍ ସ୍କ୍ରିନ୍‌ରୁ ହୁଏ',
    'shell.w3P2': 'ରିହାତି ପାଇଁ କାରଣ ଦରକାର, ଏବଂ ଦେୟ ଦିଆଯାଇଥିବା ପରିଦର୍ଶନର ସଂଶୋଧନ ଆଡଜଷ୍ଟମେଣ୍ଟ ଦ୍ୱାରା ହୁଏ',
    'shell.w3P3': 'ଆଲର୍ଜି ସୂଚନା ରୋଗୀଙ୍କ ପ୍ରତ୍ୟେକ କାର୍ଡ ଓ ପ୍ରିଣ୍ଟଆଉଟ୍‌ରେ ଦେଖାଯାଏ',
    'shell.goToStep': 'ପଦକ୍ଷେପ {n} କୁ ଯାଆନ୍ତୁ',
    'shell.skip': 'ଛାଡ଼ନ୍ତୁ',
    'shell.getStarted': 'ଆରମ୍ଭ କରନ୍ତୁ',

    'shell.enterPin': 'ଆପଣଙ୍କ PIN ଦିଅନ୍ତୁ',
    'shell.stillSignedIn': '{name} ଏହି ଡିଭାଇସ୍‌ରେ ଏବେ ବି ସାଇନ୍ ଇନ୍ ଅଛନ୍ତି।',
    'shell.deviceLocked': 'ଏହି ଡିଭାଇସ୍ ଲକ୍ ଅଛି।',
    'shell.pinEntry': 'PIN ଏଣ୍ଟ୍ରି',
    'shell.pinWrong': 'ଏହି PIN ଠିକ୍ ନୁହେଁ। ପୁଣି ଚେଷ୍ଟା କରନ୍ତୁ।',
    'shell.fourDigits': 'ଚାରି ଅଙ୍କ।',
    'shell.deleteDigit': 'ଶେଷ ଅଙ୍କ ମିଟାନ୍ତୁ',
    'shell.signOutInstead': 'ଏହା ବଦଳରେ ସାଇନ୍ ଆଉଟ୍ କରନ୍ତୁ',
    'shell.forgotPin': 'PIN ଭୁଲିଗଲେ? ସାଇନ୍ ଆଉଟ୍ କଲେ ଏହା ହଟିଯାଏ, ଏବଂ କ୍ଲିନିକ୍ ପାସୱାର୍ଡରେ ପୁଣି ସାଇନ୍ ଇନ୍ କରି ଆପଣ ନୂଆ PIN ରଖିପାରିବେ।',

    'shell.couldNotSignIn': 'ସାଇନ୍ ଇନ୍ ହୋଇପାରିଲା ନାହିଁ।',
    'shell.welcomeBack': 'ପୁଣି ସ୍ୱାଗତ',
    'shell.signInTo': '{clinic} ରେ ସାଇନ୍ ଇନ୍ କରନ୍ତୁ।',
    'shell.passwordCleared': 'ପାସୱାର୍ଡ ବକ୍ସ ଖାଲି କରାଯାଇଛି। ଯଦି ଆପଣଙ୍କ ବ୍ରାଉଜର୍ ଏହାକୁ ନିଜେ ଭରିଥିଲା, ତେବେ ସେହି ସେଭ୍ ପାସୱାର୍ଡ ପୁରୁଣା — ବର୍ତ୍ତମାନର ପାସୱାର୍ଡ ଟାଇପ୍ କରନ୍ତୁ।',
    'shell.username': 'ୟୁଜରନେମ୍',
    'shell.password': 'ପାସୱାର୍ଡ',
    'shell.hidePassword': 'ପାସୱାର୍ଡ ଲୁଚାନ୍ତୁ',
    'shell.showPassword': 'ପାସୱାର୍ଡ ଦେଖାନ୍ତୁ',
    'shell.signingIn': 'ସାଇନ୍ ଇନ୍ ହେଉଛି…',
    'shell.signIn': 'ସାଇନ୍ ଇନ୍ କରନ୍ତୁ',
    'shell.accountsByAdmin': 'ଆକାଉଣ୍ଟ କ୍ଲିନିକ୍ ଆଡମିନ୍ ତିଆରି କରନ୍ତି। ଯଦି ଆପଣ ଲକ୍ ଆଉଟ୍ ହୋଇଛନ୍ତି, ବାରମ୍ବାର ଚେଷ୍ଟା ନକରି ସେମାନଙ୍କୁ ପାସୱାର୍ଡ ରିସେଟ୍ କରିବାକୁ କୁହନ୍ତୁ।',

    'shell.help': 'ସହାୟତା',
    'shell.faq': 'ସାଧାରଣ ପ୍ରଶ୍ନ',
    'shell.contact': 'ଯୋଗାଯୋଗ',
    'shell.howSystemBehaves': 'ଏହି ସିଷ୍ଟମ୍ କିପରି କାମ କରେ',
    'shell.thisClinic': 'ଏହି କ୍ଲିନିକ୍',
    'shell.phone': 'ଫୋନ୍',
    'shell.alternate': 'ବିକଳ୍ପ ନମ୍ବର',
    'shell.opdHours': 'OPD ସମୟ',
    'shell.whoToAsk': 'କାହାକୁ ପଚାରିବେ',
    'shell.helpRunByClinic': 'ଏହି ସିଷ୍ଟମ୍ କ୍ଲିନିକ୍ ନିଜେ ଚଳାଏ। କୌଣସି ବାହାର ହେଲ୍ପ ଡେସ୍କ ନାହିଁ ଏବଂ ଆପଣଙ୍କ ରେକର୍ଡ ଅନ୍ୟ କେହି ଦେଖିପାରିବେ ନାହିଁ।',
    'shell.helpLogins': 'ଲଗଇନ୍, ଭୂମିକା ଓ ଭୁଲିଯାଇଥିବା ପାସୱାର୍ଡ — ଏସବୁ ଆପଣଙ୍କ କ୍ଲିନିକ୍ ଆଡମିନ୍ ଷ୍ଟାଫ୍ ସ୍କ୍ରିନ୍‌ରୁ ସମ୍ଭାଳନ୍ତି।',
    'shell.helpBackups': 'ବ୍ୟାକଅପ୍, ରିଷ୍ଟୋର୍ ଓ ଅଡିଟ୍ ଲଗ୍ ମଧ୍ୟ ଆଡମିନ୍‌ଙ୍କ ପାଖରେ, ଆଡମିନ୍ ସ୍କ୍ରିନ୍‌ରେ ଅଛି।',
    'shell.faq1Q': 'ସାଇନ୍ ହୋଇଥିବା ପ୍ରେସକ୍ରିପସନ୍ କାହିଁକି ବଦଳାଯାଇପାରିବ ନାହିଁ?',
    'shell.faq1A': 'ସାଇନ୍ କଲେ ସେହି ପରିଦର୍ଶନର ପ୍ରେସକ୍ରିପସନ୍ ବନ୍ଦ ହୋଇଯାଏ, ଏବଂ ସର୍ଭର କୌଣସି ପରିବର୍ତ୍ତନ ସିଧାସଳଖ ମନା କରେ। ଯାହା ବଦଳିଛି ତାହାକୁ ଆଡେଣ୍ଡମ୍ (ଅତିରିକ୍ତ ନୋଟ୍) ଭାବେ ଲେଖନ୍ତୁ, କିମ୍ବା ପ୍ରେସକ୍ରିପସନ୍ ପୁଣି ଖୋଲି ପୁଣି ସାଇନ୍ କରନ୍ତୁ — ପୁଣି ଖୋଲିବା କଥା ମଧ୍ୟ ଆଡେଣ୍ଡମ୍‌ରେ ଲେଖାଯାଏ, ଯାହାଫଳରେ ଟାଇମଲାଇନ୍‌ରେ ଏହା ଦେଖାଯାଏ।',
    'shell.faq2Q': 'ରିହାତି ପାଇଁ କାରଣ କାହିଁକି ଦରକାର?',
    'shell.faq2A': 'ଶୂନ୍ୟରୁ ଅଧିକ କୌଣସି ରିହାତି କାରଣ ବିନା ଗ୍ରହଣ ହୁଏ ନାହିଁ, ଏବଂ ଏହା କେବେ ବି ଫିଠାରୁ ଅଧିକ ହୋଇପାରିବ ନାହିଁ। କାରଣଟି ଦେୟ ସହ ସେଭ୍ ହୁଏ, ତେଣୁ ଆସନ୍ତା ମାସର ହିସାବରେ କୌଣସି ଫି ଭୁଲ୍ ଲାଗିଲେ ମଧ୍ୟ ତାହା ବୁଝାଇ ହେବ।',
    'shell.faq3Q': 'ପୂରା ସେସନ୍‌ରେ ବୁକିଂ କଲେ କେବଳ ଚେତାବନୀ କାହିଁକି ମିଳେ?',
    'shell.faq3A': 'ଗୋଟିଏ ସେସନ୍‌ରେ ସର୍ବାଧିକ ରୋଗୀ ସଂଖ୍ୟା ରହିପାରେ, କିନ୍ତୁ କ୍ଲିନିକ୍ ଦରକାର ପଡ଼ିଲେ ଲୋକଙ୍କୁ ଦେଖନ୍ତି, ଏବଂ ସଫ୍ଟୱେର୍ ଏଥିରେ ବାଧା ଦିଏ ନାହିଁ। ଅତିରିକ୍ତ ବୁକିଂ ସେହି ସେସନ୍‌ରେ ଯୋଡ଼ାଯାଏ ଏବଂ "ଓଭରଫ୍ଲୋ" ଭାବେ ଚିହ୍ନିତ ହୁଏ, ତେଣୁ ଏହା ଅଟକି ନ ଯାଇ ଦେଖାଯାଏ।',
    'shell.faq4Q': 'ଫଲୋ-ଅପ୍ ଫି ସମୟସୀମା କିପରି କାମ କରେ?',
    'shell.faq4A': 'ପ୍ରତ୍ୟେକ ଡାକ୍ତରଙ୍କର ଦିନ ହିସାବରେ ଏକ ଫଲୋ-ଅପ୍ ସମୟସୀମା ଥାଏ। ସେହି ଡାକ୍ତର ପୂର୍ବରୁ ଦେଖିଥିବା ରୋଗୀ ଏହି ସୀମା ଭିତରେ ଫେରିଲେ, ପରିଦର୍ଶନ ଫଲୋ-ଅପ୍ ଗଣାଯାଏ ଓ ଫଲୋ-ଅପ୍ ଫି ଲାଗେ; ସୀମା ପରେ ତାହା ରିଭ୍ୟୁ ହୁଏ ଓ ପୂରା ପରାମର୍ଶ ଫି ଲାଗେ। ସେହି ଡାକ୍ତର କେବେ ଦେଖିନଥିବା ରୋଗୀ ସବୁବେଳେ ନୂଆ।',
    'shell.faq5Q': 'ଭାଇଟାଲ୍ସ ସଂଶୋଧନ ବଦଳରେ କାହିଁକି ଯୋଡ଼ାଯାଏ?',
    'shell.faq5A': 'ପ୍ରତ୍ୟେକ ରିଡିଂ ନିଜ ସମୟ ସହ ଅଲଗା ସେଭ୍ ହୁଏ। ସକାଳର BP ଓ ସନ୍ଧ୍ୟାର — ଦୁଇଟି ଠିକ୍, ତେଣୁ ପରିଦର୍ଶନରେ ଦୁଇଟି କ୍ରମରେ ଦେଖାଯାଏ; ଦ୍ୱିତୀୟଟି ପ୍ରଥମକୁ ମିଟାଏ ନାହିଁ।',
    'shell.faq6Q': 'ଟୋକେନ୍ ନମ୍ବରର ଅର୍ଥ କ’ଣ?',
    'shell.faq6A': 'ଟୋକେନ୍ ପ୍ରତ୍ୟେକ ଡାକ୍ତରଙ୍କ ଦୈନିକ ପରିଦର୍ଶନ ଗଣେ ଏବଂ ପ୍ରତି ସକାଳେ ପୁଣି ଆରମ୍ଭ ହୁଏ। ଟୋକେନ୍ 4 ଅର୍ଥ ଆଜି ସେହି ଡାକ୍ତରଙ୍କ ପାଇଁ ଖୋଲାଯାଇଥିବା ଚତୁର୍ଥ ପରିଦର୍ଶନ, ପୂରା କ୍ଲିନିକ୍‌ର ଚତୁର୍ଥ ରୋଗୀ ନୁହେଁ — ତେଣୁ ଦୁଇ ଡାକ୍ତର ଏକାସାଙ୍ଗରେ ଟୋକେନ୍ 4 ଡାକିପାରନ୍ତି।',
    'shell.faq7Q': 'ଦେୟ କିପରି ସଂଶୋଧନ କରିବେ?',
    'shell.faq7A': 'ଯେଉଁ ପରିଦର୍ଶନର ଦେୟ ହୋଇସାରିଛି, ତାହାର ଦେୟରେ ଆଉ ପରିବର୍ତ୍ତନ ହୁଏ ନାହିଁ। ଫେରସ୍ତ କିମ୍ବା ସଂଶୋଧନକୁ ଆଡଜଷ୍ଟମେଣ୍ଟ ଭାବେ ଲେଖନ୍ତୁ: ମୂଳ ଏଣ୍ଟ୍ରି ଯେମିତି ଥିଲା ସେମିତି ରହେ ଏବଂ ଆଡଜଷ୍ଟମେଣ୍ଟ ତା’ ପାଖରେ ଯୋଡ଼ାଯାଏ, ତେଣୁ ଦିନର ହିସାବ ଠିକ୍ ରହେ।',
    'shell.faq8Q': 'ଷ୍ଟାଫ୍ ଲଗଇନ୍ ବନ୍ଦ କଲେ କ’ଣ ହୁଏ?',
    'shell.faq8A': 'ସାଇନ୍ ଇନ୍ ବେଳେ ଜଣାଯାଏ ଯେ ଆକାଉଣ୍ଟ ବନ୍ଦ କରାଯାଇଛି, ଆଉ କିଛି ବଦଳେ ନାହିଁ — ସେମାନଙ୍କ ପୂର୍ବ ପରିଦର୍ଶନ, ପ୍ରେସକ୍ରିପସନ୍ ଓ ଅଡିଟ୍ ଏଣ୍ଟ୍ରି ସେମାନଙ୍କ ନାମରେ ହିଁ ରହେ। ଆଡମିନ୍ ନିଜ ଆକାଉଣ୍ଟ ବନ୍ଦ କରିପାରିବେ ନାହିଁ, ତେଣୁ କ୍ଲିନିକ୍ ନିଜକୁ ଲକ୍ ଆଉଟ୍ କରିପାରିବ ନାହିଁ।',
    'shell.faq9Q': 'ଆଲର୍ଜି ବ୍ୟାନର୍ ପ୍ରତ୍ୟେକ ସ୍କ୍ରିନ୍‌ରେ ନାଲି କାହିଁକି?',
    'shell.faq9A': 'ଆଲର୍ଜି ରୋଗୀଙ୍କ ସୂଚନାରେ ରହେ ଏବଂ ଧାଡ଼ି, ପରିଦର୍ଶନ, ଆପଏଣ୍ଟମେଣ୍ଟ କାର୍ଡ ଓ ଛପା ଲେଟରହେଡ୍‌ରେ ପୁଣି ଦେଖାଯାଏ। ଏହା ହିଁ ଏକମାତ୍ର ସୂଚନା ଯାହାକୁ ଏତେ ସ୍ପଷ୍ଟ ଭାବେ ଦେଖାଯାଏ, କାରଣ ଛାଡ଼ିଯାଇଥିବା ଆଲର୍ଜି ହେଉଛି ସବୁଠାରୁ ମହଙ୍ଗା ଭୁଲ୍, ଯାହାକୁ ଏଡ଼ାଇବାରେ ଏହି ସିଷ୍ଟମ୍ ସାହାଯ୍ୟ କରିପାରେ।',
    'shell.faq10Q': 'ବ୍ୟାକଅପ୍ ଓ ରିଷ୍ଟୋର୍ କିପରି କାମ କରେ?',
    'shell.faq10A': 'ଆଡମିନ୍ ଆଡମିନ୍ ସ୍କ୍ରିନ୍‌ରୁ ବ୍ୟାକଅପ୍ ନିଅନ୍ତି; ଫାଇଲ୍ କ୍ଲିନିକ୍‌ର ବ୍ୟାକଅପ୍ ଫୋଲ୍ଡରରେ ଲେଖାଯାଏ ଏବଂ ପ୍ରକୃତରେ ଡିସ୍କରେ ସେଭ୍ ହେବା ପରେ ହିଁ ସଫଳ ବୋଲି ଜଣାଯାଏ। ରିଷ୍ଟୋର୍ ଆପଣ ବାଛିଥିବା ଫାଇଲ୍‌ରୁ ବର୍ତ୍ତମାନର ଡାଟାବେସ୍ ବଦଳାଇଦିଏ, ଏବଂ ଦୁଇଟି କାମ ଅଡିଟ୍ ଲଗ୍‌ରେ ଲେଖାଯାଏ।',

    'shell.searchFailed': 'ଖୋଜା ହୋଇପାରିଲା ନାହିଁ।',
    'shell.searchAria': 'ରୋଗୀ, ଡାକ୍ତର ଓ ରେକର୍ଡ ଖୋଜନ୍ତୁ',
    'shell.searchPh': 'ରୋଗୀ, ଡାକ୍ତର କିମ୍ବା ନିଦାନ',
    'shell.clear': 'ସଫା କରନ୍ତୁ',
    'shell.records': 'ରେକର୍ଡ',
    'shell.recent': 'ସାମ୍ପ୍ରତିକ ଖୋଜା',
    'shell.nothingSearched': 'ଏପର୍ଯ୍ୟନ୍ତ କିଛି ଖୋଜାଯାଇନାହିଁ',
    'shell.nothingSearchedBody': 'ଆରମ୍ଭ ପାଇଁ ଦୁଇଟି ଅକ୍ଷର ଯଥେଷ୍ଟ। ପରିବାରର ମୋବାଇଲ୍ ନମ୍ବର ପ୍ରାୟ ଗୋଟିଏ ଥାଏ, ତେଣୁ ନାମରେ ଖୋଜିବା ଅଧିକ ଶୀଘ୍ର ହୁଏ।',
    'shell.noMatchFor': '“{term}” ପାଇଁ କିଛି ମିଳିଲା ନାହିଁ',
    'shell.noMatchHint': 'କମ୍ ଅକ୍ଷର ଦେଇ ଦେଖନ୍ତୁ, କିମ୍ବା ଅନ୍ୟ ଟ୍ୟାବ୍‌ରେ ଖୋଜନ୍ତୁ।',
    'shell.allergyLine': 'ଆଲର୍ଜି: {allergies}',
    'shell.visitCount_one': '{n}ଟି ପରିଦର୍ଶନ',
    'shell.visitCount_other': '{n}ଟି ପରିଦର୍ଶନ',
    'shell.specialtyFallback': 'ସାଧାରଣ ଚିକିତ୍ସା',
    'shell.greetingFallback': 'ଆଜ୍ଞା',

    'shell.dayAtTime': '{day}, {time}',
    'shell.serialN': 'କ୍ରମାଙ୍କ #{n}',
    'shell.serialOfTotal': 'କ୍ରମାଙ୍କ #{n} ({total} ମଧ୍ୟରୁ)',
    'shell.serialWithDoctor': 'କ୍ରମାଙ୍କ #{n} · ବର୍ତ୍ତମାନ ଡାକ୍ତରଙ୍କ ପାଖରେ',
    'shell.serialNextAfter': 'କ୍ରମାଙ୍କ #{n} · #{now} ପରେ ପରବର୍ତ୍ତୀ ପାଳି',
    'shell.serialNext': 'କ୍ରମାଙ୍କ #{n} · ପରବର୍ତ୍ତୀ ପାଳି',
    'shell.serialNowServing': 'କ୍ରମାଙ୍କ #{n} · ବର୍ତ୍ତମାନ #{now} ଚାଲିଛି',
    'shell.serialNextUp': 'କ୍ରମାଙ୍କ #{n} · ପରବର୍ତ୍ତୀ #{next}',
    'shell.serialAria': 'କ୍ରମାଙ୍କ {n}',
    'shell.serialAriaActive': 'କ୍ରମାଙ୍କ {n}, ବର୍ତ୍ତମାନ ଡାକ୍ତରଙ୍କ ପାଖରେ',
    'shell.serialAriaDone': 'କ୍ରମାଙ୍କ {n}, ସମ୍ପୂର୍ଣ୍ଣ',

    'shell.missed': 'ଛୁଟିଗଲା',
    'shell.nowServing': 'ବର୍ତ୍ତମାନ ଚାଲିଛି',
    'shell.bookingDetails': 'ବୁକିଂ ବିବରଣୀ',
    'shell.consultation': 'ପରାମର୍ଶ',
    'shell.clinic': 'କ୍ଲିନିକ୍',
    'shell.payNow': 'ଏବେ ଦେୟ ଦିଅନ୍ତୁ',
    'shell.missedDesk': 'ଏହି ଦିନ ଚେକ୍-ଇନ୍ ବିନା ବିତିଗଲା। ଏହାକୁ "ଆସିଲେ ନାହିଁ" ଲେଖନ୍ତୁ, ସମୟ ବଦଳାନ୍ତୁ କିମ୍ବା ବାତିଲ୍ କରନ୍ତୁ।',
    'shell.missedPatient': 'ଏହି ତାରିଖ ବିତିଗଲାଣି। ଏବେ ବି ଦେଖାଇବାକୁ ଚାହୁଁଥିଲେ ନୂଆ ଆପଏଣ୍ଟମେଣ୍ଟ ବୁକ୍ କରନ୍ତୁ।',
    'shell.markNoShow': '"ଆସିଲେ ନାହିଁ" ଲେଖନ୍ତୁ',
    'shell.checkIn': 'ଚେକ୍-ଇନ୍ କରନ୍ତୁ',

    'shell.slideOf': '{count} ମଧ୍ୟରୁ {i} ଦେଖାନ୍ତୁ',
    'shell.prevDay': 'ପୂର୍ବ ଦିନ',
    'shell.nextDay': 'ପରଦିନ',
    'shell.shareOnWhatsApp': 'WhatsApp ରେ ପଠାନ୍ତୁ',
    'shell.copied': 'କପି ହେଲା। ଯେକୌଣସି ଆପ୍‌ରେ ପେଷ୍ଟ କରନ୍ତୁ।',
    'shell.copyFailed': 'ଏହି ଡିଭାଇସ୍‌ରେ କପି ହୋଇପାରିଲା ନାହିଁ।',
    'shell.shareTitle': '{title} ପଠାନ୍ତୁ',
    'shell.textMessage': 'ମେସେଜ୍ (SMS)',
    'shell.copyText': 'ଟେକ୍ସଟ୍ କପି କରନ୍ତୁ',
    'shell.nMore': 'ଆଉ {n}ଟି',
    'shell.searchPatients': 'ରୋଗୀ ଖୋଜନ୍ତୁ',
    'shell.patientSearchPh': 'ନାମ, ମୋବାଇଲ୍ କିମ୍ବା ରୋଗୀ କୋଡ୍',
    'shell.noPatientMatch': '“{q}” ସହ ମେଳ ଖାଉଥିବା କୌଣସି ରୋଗୀ ମିଳିଲେ ନାହିଁ।',
    'shell.prevDoctor': 'ପୂର୍ବ ଡାକ୍ତର',
    'shell.nextDoctor': 'ପରବର୍ତ୍ତୀ ଡାକ୍ତର',
  },
})
